import cn from "classnames";
import BookShow from "components/shared/book/show";
import Loader from "components/shared/loader";
import { dateTimeToView } from "helpers/date";
import { logistTaskStatuses, logistTaskStatusesEnum, logistTaskTypesEnum, trackerRelationTypes } from "helpers/enums";
import { serializeReq } from "helpers/form";
import { paths } from "helpers/paths";
import { viewContainer } from "helpers/redux";
import { enumLabel, formatNumber } from "helpers/system";
import { requiredField } from "helpers/validators";
import policy from "policies";
import React from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { cancel, complete } from "sources/books/tasks";
import CancelTaskForm from "./cancelTaskForm";
import TaskMapView from "./map";

const config = {
    comment: {
        inputProps: { name: "comment" },
        textarea: true,
        label: "Комментарий",
        labelWrapper: "col-12 col-form-label col-form-label-sm",
        fieldWrapper: "col-12",
        validators: [(value, data) => (data.commentRequired ? requiredField(value) : "")],
    },
    commentRequired: {},
    id: {},
};

class ShowLogistTask extends BookShow {
    componentDidMount() {
        this.context?.setPrevItemId(Number(this.props.match.params.id));
        const initial = this.props.initialState;
        this.setState({ data: initial, initial, shouldUpdate: true });
        this.getItem(this.props).then((data) => {
            if (data.status === logistTaskStatusesEnum.New && data.isMine) {
                this.props.source
                    .setAsViewed({ id: data.id })
                    .then(() => this.setState({ data: { ...data, status: logistTaskStatusesEnum.Viewed } }));
            }
        });
    }

    getItem = ({ source, match }) => {
        const defParams = { isLoading: false, isValid: true, shouldUpdate: false };
        return source.getItem(match.params).then((data) => {
            this.setState({ data, initial: data, ...defParams });
            return data;
        });
    };

    renderHead = () => {
        const { back } = this.props;
        const { status } = this.state.data || {};
        return (
            <div className="book-show__head">
                <div className="book-show__close close close--sm" onClick={back} />
                <div className="book-show__title">Задача #{this.state.data?.id}</div>
                {status && (
                    <div className="align-items-center">
                        <div
                            className={cn("badge", {
                                "badge-primary": [logistTaskStatusesEnum.New, logistTaskStatusesEnum.Viewed].includes(
                                    status,
                                ),
                                "badge-success": status === logistTaskStatusesEnum.Completed,
                                "badge-secondary": status === logistTaskStatusesEnum.Canceled,
                                "badge-danger": status === logistTaskStatusesEnum.Failed,
                            })}
                        >
                            {enumLabel(logistTaskStatuses, this.state.data?.status)}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    renderBodyWrap = () => {
        const { isLoading } = this.state;
        if (isLoading) {
            return <Loader prefix="absolute-center" />;
        }
        return (
            <div className="native-scroll__wrap h-100">
                <Scrollbars>
                    <div className="native-scroll__container">{this.renderBody()}</div>
                </Scrollbars>
            </div>
        );
    };

    complete = () => {
        const { mergeParams } = this.props;
        const { isLoading, data } = this.state;
        if (!isLoading) {
            this.setState({ isLoading: true });
            return complete({ id: data.id }).then((res) => {
                if (res) {
                    this.update(mergeParams);
                }
                this.setState({ isLoading: false });
            });
        }
    };

    cancel = () => {
        const { mergeParams, modal } = this.props;
        const cancelAction = (cancelData) => {
            const { isLoading, data } = this.state;
            if (!isLoading) {
                this.setState({ isLoading: true });
                return cancel({ id: data.id, ...cancelData }).then((res) => {
                    if (res) {
                        this.update(mergeParams);
                    }
                    this.setState({ isLoading: false });
                });
            }
        };
        modal({
            name: "default",
            title: "Выберите причину отказа",
            windowMod: "sm",
            CustomBody: (props) => (
                <CancelTaskForm {...this.props} {...props} type={this.state.data.type} cancelAction={cancelAction} />
            ),
        });
    };

    renderActions = () => {
        const { type, commentRequired } = this.state.data;
        switch (type) {
            case logistTaskTypesEnum.NotificationType: {
                return (
                    <div className="card card-body">
                        {this.renderField({
                            ...this.props.config.comment,
                            required: commentRequired,
                            validators: commentRequired ? [requiredField] : [],
                        })}
                        {this.renderButton()}
                    </div>
                );
            }
            case logistTaskTypesEnum.NeedBidForServiceWork: {
                return null;
            }
            case logistTaskTypesEnum.ApprovingServiceWorkInBid:
            case logistTaskTypesEnum.ApprovingServiceWorkInEmptyMileageBid:
            case logistTaskTypesEnum.NeedDriverShift: {
                return (
                    <div className="card card-body">
                        <div className="panel__row panel__row--button">
                            <div className="btn btn-secondary btn-sm mr-2" onClick={this.cancel}>
                                Отказать
                            </div>
                            <div className="btn btn-brand btn-sm" onClick={this.complete}>
                                Согласовать
                            </div>
                        </div>
                    </div>
                );
            }
            default:
                return null;
        }
    };

    renderButton = () => {
        return (
            <div className="panel__row panel__row--button">
                <div className="btn btn-brand btn-sm" onClick={this.apply}>
                    Отклонение отработано
                </div>
            </div>
        );
    };

    apply = () => {
        const { source, mergeParams, config } = this.props;
        const { isLoading, data } = this.state;
        if (!isLoading) {
            if (this.isValidForm(data)) {
                this.setState({ isLoading: true, isValid: true });
                return source
                    .apply(serializeReq({ id: data.id, comment: data.comment }, config))
                    .then((res) =>
                        res ? this.update(mergeParams) : this.setState({ isLoading: false, isValid: false }),
                    );
            }
            this.setState({ isValid: false });
        }
    };

    formatTempLimit = (val) => {
        if (val == null) {
            return "";
        }
        const formatted = formatNumber(val);
        return val > 0 ? `+${formatted}` : `${formatted}`;
    };

    formatTemperatureRegimeRange = (min, max) => {
        if (min != null && max != null) {
            return `от ${this.formatTempLimit(min)} до ${this.formatTempLimit(max)} °C`;
        }
        if (min != null) {
            return `от ${this.formatTempLimit(min)} °C`;
        }
        if (max != null) {
            return `до ${this.formatTempLimit(max)} °C`;
        }
        return "не задана";
    };

    formatTemperatureValue = (value) => {
        if (value == null) {
            return "";
        }
        const formatted = formatNumber(value);
        return value > 0 ? `+${formatted} °C` : `${formatted} °C`;
    };

    getSensorLabel = (sensorName, trackerRelationType) => {
        if (!trackerRelationType) {
            return sensorName;
        }
        const label = enumLabel(trackerRelationTypes, trackerRelationType);
        return label && label !== "Без связи" ? `${sensorName} (${label.toLowerCase()})` : sensorName;
    };

    renderTemperatureValueInfo = () => {
        const { temperatureValue } = this.state.data || {};
        if (!temperatureValue) {
            return null;
        }

        const { values, temperatureMinimum, temperatureMaximum, fixedAt } = temperatureValue;
        const trackerRelationType = temperatureValue.trackerRelationType || temperatureValue.TrackerRelationType;
        const deviations = [];

        if (values && Array.isArray(values)) {
            values.forEach((value, index) => {
                if (value != null) {
                    if (temperatureMaximum != null && value > temperatureMaximum) {
                        deviations.push({
                            sensorName: this.getSensorLabel(`№ ${index + 1}`, trackerRelationType),
                            actual: value,
                            diff: value - temperatureMaximum,
                            type: "above",
                        });
                    } else if (temperatureMinimum != null && value < temperatureMinimum) {
                        deviations.push({
                            sensorName: this.getSensorLabel(`№ ${index + 1}`, trackerRelationType),
                            actual: value,
                            diff: temperatureMinimum - value,
                            type: "below",
                        });
                    }
                }
            });
        }

        return (
            <div className="pb-4">
                <b>Зафиксировано отклонение от температурного режима.</b>
                <div className="mt-2">
                    <div>
                        Допустимая температура:{" "}
                        <b>{this.formatTemperatureRegimeRange(temperatureMinimum, temperatureMaximum)}</b>.
                    </div>
                    {deviations.length > 0 ? (
                        deviations.map((dev, idx) => (
                            <div key={idx} className={idx > 0 ? "mt-3 pt-3 border-top" : ""}>
                                {deviations.length > 1 && (
                                    <div className="font-weight-bold text-dark mt-2">Датчик {dev.sensorName}</div>
                                )}
                                <div>
                                    Фактическая температура: <b>{this.formatTemperatureValue(dev.actual)}</b>.
                                </div>
                                <div>
                                    Отклонение:{" "}
                                    <b>
                                        {dev.type === "above" ? "выше" : "ниже"} допустимого значения на{" "}
                                        {formatNumber(dev.diff)} °C
                                    </b>
                                    .
                                </div>
                                {fixedAt && (
                                    <div>
                                        Время фиксации: <b>{dateTimeToView(fixedAt, "DD.MM.YYYY, HH:mm")}</b>.
                                    </div>
                                )}
                                {deviations.length === 1 && (
                                    <div>
                                        Датчик: <b>{dev.sensorName}</b>.
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div>
                            {fixedAt && (
                                <div>
                                    Время фиксации: <b>{dateTimeToView(fixedAt, "DD.MM.YYYY, HH:mm")}</b>.
                                </div>
                            )}
                            <div className="text-muted mt-1">Нет зафиксированных отклонений по датчикам.</div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    renderInfoByType = () => {
        const { firstBidPoint, lastBidPoint, serviceWork, driverShift } = this.state.data || {};
        return (
            <div>
                {this.renderTemperatureValueInfo()}
                {firstBidPoint && (
                    <div className="pb-4">
                        <b>Точка загрузки</b>
                        <div className="text-muted">{firstBidPoint.geozone.address}</div>
                        <div>
                            Плановая дата въезда: <b>{dateTimeToView(firstBidPoint.planEnterDateOffset)}</b>
                        </div>
                        <div>
                            Плановая дата выезда: <b>{dateTimeToView(firstBidPoint.planLeaveDateOffset)}</b>
                        </div>
                    </div>
                )}
                {lastBidPoint && (
                    <div className="pb-4">
                        <b>Точка выгрузки</b>
                        <div className="text-muted">{lastBidPoint.geozone.address}</div>
                        <div>
                            Плановая дата въезда: <b>{dateTimeToView(lastBidPoint.planEnterDateOffset)}</b>
                        </div>
                        <div>
                            Плановая дата выезда: <b>{dateTimeToView(lastBidPoint.planLeaveDateOffset)}</b>
                        </div>
                    </div>
                )}
                {serviceWork && (
                    <div className="pb-4">
                        <b>Сервисная работа</b>
                        <div>
                            Адрес: <b>{serviceWork.address}</b>
                        </div>
                        {serviceWork.addresses && (
                            <div>
                                Адреса СТО:&nbsp;
                                <b>
                                    {serviceWork.addresses.map((addr) => (
                                        <div key={addr}>{addr}</div>
                                    ))}
                                </b>
                            </div>
                        )}
                        <div>
                            Плановая дата начала: <b>{dateTimeToView(serviceWork.startDate)}</b>
                        </div>
                        <div>
                            Плановая дата завершения: <b>{dateTimeToView(serviceWork.endDate)}</b>
                        </div>
                        {serviceWork.type && (
                            <div>
                                Тип работы: <b>{serviceWork.type}</b>
                            </div>
                        )}
                        {serviceWork.comment && (
                            <div>
                                Комментарий: <b>{serviceWork.comment}</b>
                            </div>
                        )}
                    </div>
                )}
                {driverShift && (
                    <div className="pb-4">
                        <b>Пересменка</b>
                        <div>
                            Адрес: <b>{driverShift.address}</b>
                        </div>
                        <div>
                            Плановая дата: <b>{dateTimeToView(driverShift.startDate)}</b>
                        </div>
                        <div>
                            ФИО водителя (сменяемого): <b>{driverShift.driverName}</b>
                        </div>
                        {driverShift.nextDriver?.user && (
                            <div>
                                ФИО водителя (нового): <b>{driverShift.nextDriver.user.fullName}</b>{" "}
                                <span className="text-muted">{driverShift.nextDriver.user.phoneNumber}</span>
                            </div>
                        )}
                        {driverShift.comment && (
                            <div>
                                Комментарий: <b>{driverShift.comment}</b>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    renderBody = () => {
        const {
            bidId,
            car,
            driver,
            trailer,
            createdAt,
            completedAt,
            status,
            comment,
            isMine,
            deadline,
            userName,
            name,
            type,
            groupName,
            groupId,
            updatedBy,
        } = this.state.data;
        const isOnlyExtendedViewer = policy("user", { user: { roles: this.props.roles } }).isOnlyExtendedViewer();
        return (
            <>
                <div className="card card-body font-size-14 mb-4">
                    <h6 className="pb-4">{name}</h6>
                    <div className="row">
                        <div className="col-md-6 border-right">
                            <div className="row pb-2">
                                <div className="col-sm-5 col-md-4 text-sm-right">
                                    <div className="text-muted">Дата создания</div>
                                </div>
                                <div className="col-sm-7 col-md-8">{dateTimeToView(createdAt)}</div>
                            </div>
                            <div className="row pb-2">
                                <div className="col-sm-5 col-md-4 text-sm-right">
                                    <div className="text-muted">Срок</div>
                                </div>
                                <div className="col-sm-7 col-md-8">{dateTimeToView(deadline)}</div>
                            </div>
                            {completedAt && (
                                <div className="row pb-2">
                                    <div className="col-sm-5 col-md-4 text-sm-right">
                                        <div className="text-muted">Дата выполнения</div>
                                    </div>
                                    <div className="col-sm-7 col-md-8">{dateTimeToView(completedAt)}</div>
                                </div>
                            )}
                            {userName && (
                                <div className="row pb-2">
                                    <div className="col-sm-5 col-md-4 text-sm-right">
                                        <div className="text-muted">Ответственный</div>
                                    </div>
                                    <div className="col-sm-7 col-md-8">{userName}</div>
                                </div>
                            )}
                            {updatedBy && (
                                <div className="row pb-2">
                                    <div className="col-sm-5 col-md-4 text-sm-right">
                                        <div className="text-muted">Автор последнего изменения статуса</div>
                                    </div>
                                    <div className="col-sm-7 col-md-8">{updatedBy.displayName}</div>
                                </div>
                            )}
                            {groupId && (
                                <div className="row pb-2">
                                    <div className="col-sm-5 col-md-4 text-sm-right">
                                        <div className="text-muted">Группа пользователей</div>
                                    </div>
                                    <div className="col-sm-7 col-md-8">
                                        <a
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            href={paths.usersGroupsShow(groupId)}
                                        >
                                            {groupName ?? groupId}
                                        </a>
                                    </div>
                                </div>
                            )}
                            {bidId && (
                                <div className="row pb-2">
                                    <div className="col-sm-5 col-md-4 text-sm-right">
                                        <div className="text-muted">Заявка</div>
                                    </div>
                                    <div className="col-sm-7 col-md-8">
                                        <a target="_blank" rel="noopener noreferrer" href={paths.bidShow("bid", bidId)}>
                                            {bidId}
                                        </a>
                                    </div>
                                </div>
                            )}
                            <div className="row pb-2">
                                <div className="col-sm-5 col-md-4 text-sm-right">
                                    <div className="text-muted">Водитель</div>
                                </div>
                                <div className="col-sm-7 col-md-8">
                                    {driver?.id ? (
                                        <>
                                            <a
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href={paths.driversShow(driver.id)}
                                            >
                                                {driver.user.fullName}
                                            </a>
                                            <br />
                                            <small className="text-muted">{driver.user.phoneNumber}</small>
                                        </>
                                    ) : (
                                        <>&mdash;</>
                                    )}
                                </div>
                            </div>
                            <div className="row pb-2">
                                <div className="col-sm-5 col-md-4 text-sm-right">
                                    <div className="text-muted">Машина</div>
                                </div>
                                <div className="col-sm-7 col-md-8">
                                    {car?.id ? (
                                        <a target="_blank" rel="noopener noreferrer" href={paths.trucksShow(car.id)}>
                                            {car.number}
                                        </a>
                                    ) : (
                                        <>&mdash;</>
                                    )}
                                </div>
                            </div>
                            <div className="row pb-2">
                                <div className="col-sm-5 col-md-4 text-sm-right">
                                    <div className="text-muted">Прицеп</div>
                                </div>
                                <div className="col-sm-7 col-md-8">
                                    {trailer?.id ? (
                                        <a
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            href={paths.trailersShow(trailer.id)}
                                        >
                                            {trailer.number}
                                        </a>
                                    ) : (
                                        <>&mdash;</>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">{this.renderInfoByType()}</div>
                    </div>
                </div>
                <TaskMapView tileUrls={this.props.tileUrls} data={this.state.data} />
                <div className="mb-4">
                    {[logistTaskStatusesEnum.New, logistTaskStatusesEnum.Viewed].includes(status) &&
                    isMine &&
                    !isOnlyExtendedViewer
                        ? this.renderActions()
                        : type === logistTaskTypesEnum.NotificationType && (
                              <div className="card card-body">
                                  <label className="col-form-label col-form-label-sm text-muted">Комментарий</label>
                                  <div>{comment || "—"}</div>
                              </div>
                          )}
                </div>
            </>
        );
    };
}

const ConfiguredShow = (props) => <ShowLogistTask {...props} config={config} initialState={{ comment: "" }} />;

export default viewContainer(ConfiguredShow, "tasks");
